// Shared TinyMCE `init` for the mergetags editors (formula expressions and
// footnote text). Both render `{…}` tokens as chips and offer the same
// type-`{`-to-search autocompleter; they differ only in their token grammar, so
// the plumbing lives here and the grammar is passed in.

import { useMemo, useRef } from "react";
import type { TinyMCEEditor } from "@jsq/ds/core";

/** A menu entry, in the shape the mergetags plugin expects. */
export type MergeTagsEditorTag = {
  /** Bare token the plugin wraps in its prefix/suffix, e.g. `d.commitment_1`. */
  value: string;
  /** Label shown in the menu and matched against what the user types. */
  title: string;
};

type MergeTagsEditorOptions = {
  /** Autocompleters are registered on the editor by name, so keep this unique. */
  autocompleterId: string;
  placeholder: string;
  height: number;
  tags: ReadonlyArray<MergeTagsEditorTag>;
  /** Closing delimiter the plugin renders after a token's value. */
  mergetagsSuffix: string;
  /** Wraps a chosen tag's bare value into the grammar's complete token. */
  formatToken: (value: string) => string;
};

// TinyMCE's own class names for the scrolling list inside the autocompleter
// and the option the keyboard is on.
const MENU_SELECTOR = ".tox-autocompleter .tox-menu";
const HIGHLIGHTED_OPTION_SELECTOR = ".tox-autocompleter .tox-collection__item--active";

/** Scrolls the keyboard-highlighted option back into view.
 *
 * The autocompleter marks its current option with a class and
 * `aria-activedescendant` instead of moving DOM focus, so the browser never
 * scrolls it into view the way it does for a focused element. Without this,
 * arrowing past the last visible option walks the highlight off the bottom of
 * the list and the list itself never moves. Scrolls by the smallest amount that
 * reveals the option, like `scrollIntoView({ block: "nearest" })`. */
const revealHighlightedOption = () => {
  const menu = document.querySelector<HTMLElement>(MENU_SELECTOR);
  const option = document.querySelector<HTMLElement>(HIGHLIGHTED_OPTION_SELECTOR);
  if (!menu || !option) return;

  const optionBox = option.getBoundingClientRect();
  const menuBox = menu.getBoundingClientRect();
  if (optionBox.top < menuBox.top) {
    menu.scrollTop -= menuBox.top - optionBox.top;
  } else if (optionBox.bottom > menuBox.bottom) {
    menu.scrollTop += optionBox.bottom - menuBox.bottom;
  }
};

/** Watches the open autocompleter and keeps its highlighted option visible.
 *
 * TinyMCE builds the menu asynchronously and owns the node, so rather than race
 * it for a reference we watch for the highlight class landing anywhere while the
 * autocompleter is open. Class changes are cheap to observe and the callback
 * no-ops until both the list and a highlighted option exist. */
const keepHighlightedOptionVisible = (editor: NonNullable<TinyMCEEditor["editor"]>) => {
  let observer: MutationObserver | null = null;

  editor.on("AutocompleterStart", () => {
    if (observer) return;
    observer = new MutationObserver(revealHighlightedOption);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
    });
  });

  editor.on("AutocompleterEnd remove", () => {
    observer?.disconnect();
    observer = null;
  });
};

export const useMergeTagsEditorInit = ({
  autocompleterId,
  placeholder,
  height,
  tags,
  mergetagsSuffix,
  formatToken,
}: MergeTagsEditorOptions) => {
  // `setup` runs once per editor, so the autocompleter closes over whatever was
  // current at mount. Reading through refs keeps `fetch` on the live tag list.
  const tagsRef = useRef(tags);
  tagsRef.current = tags;
  const formatTokenRef = useRef(formatToken);
  formatTokenRef.current = formatToken;

  return useMemo(
    () => ({
      plugins: "mergetags",
      ui_mode: "split" as const,
      toolbar: false as const,
      menubar: false,
      statusbar: false,
      placeholder,
      mergetags_prefix: "{",
      mergetags_suffix: mergetagsSuffix,
      mergetags_list: tags,
      height,
      content_style:
        "body { font-family: sans-serif; font-size: 14px; direction: ltr; unicode-bidi: normal; }",
      setup: (editor: TinyMCEEditor["editor"]) => {
        if (!editor) return;
        editor.ui.registry.addAutocompleter(autocompleterId, {
          trigger: "{",
          minChars: 0,
          columns: 1,
          // Only offer the menu mid-content: a `{` typed after whitespace (or at
          // the start) is the user writing a literal brace, not reaching for a tag.
          matches: (rng) => {
            const container = rng.startContainer;
            if (container.nodeType !== Node.TEXT_NODE) return false;
            const offsetBeforeTrigger = rng.startOffset - 1;
            if (offsetBeforeTrigger < 0) return false;
            const charBefore = (container as Text).data.charAt(offsetBeforeTrigger);
            return charBefore !== "" && /\S/.test(charBefore);
          },
          fetch: (pattern: string) => {
            const lower = pattern.toLowerCase();
            const matches = tagsRef.current
              .filter((tag) => tag.title.toLowerCase().includes(lower))
              .map((tag) => ({ value: tag.value, text: tag.title }));
            return Promise.resolve(matches);
          },
          onAction: (api, rng, value: string) => {
            editor.selection.setRng(rng);
            editor.insertContent(formatTokenRef.current(value));
            api.hide();
          },
        });
        keepHighlightedOptionVisible(editor);
      },
    }),
    [autocompleterId, height, mergetagsSuffix, placeholder, tags],
  );
};
